/**
 * Componente funcional para renderizar un campo de texto personalizado.
 * @module CustomTextField
 * @param {object} props - Propiedades del componente.
 * @param {string} props.label - Etiqueta del campo de texto.
 * @param {string} [props.type="text"] - Tipo de entrada del campo de texto.
 * @param {boolean} [props.required=true] - Indica si el campo es obligatorio.
 * @param {number} [props.grid=12] - Tamaño del grid del componente.
 * @param {string} [props.width] - Ancho del campo de texto.
 * @param {string} props.value - Valor del campo de texto.
 * @param {string} props.name - Nombre del campo de texto.
 * @param {number} [props.multiline] - Número de líneas si el campo es de varias líneas.
 * @param {function} props.onChange - Función manejadora del cambio de valor del campo.
 * @param {object} [props.InputProps] - Propiedades adicionales para el componente de entrada.
 * @param {object} [props.InputLabelProps] - Propiedades adicionales para la etiqueta del campo.
 * @param {number} [props.maxLength] - Longitud máxima permitida para el campo de texto.
 * @param {boolean} [props.disabled] - Indica si el campo está deshabilitado.
 * @param {object} [props.searchPxRef] - Referencia para el campo de búsqueda.
 * @param {function} [props.onKeyPress] - Función manejadora del evento de presionar tecla.
 * @param {function} [props.onBlur] - Función manejadora del evento de tocar los input en la versión móvil.
 * @returns {JSX.Element} Elemento de React que representa el campo de texto personalizado.
 */

import React from "react";
import { Grid, useTheme } from "@mui/material";
import CustomText from "./CustomText";
import { CssTextField } from "../CSS/CssTextField";

const CustomTextField = ({
  label,
  type = "text",
  required = false,
  width,
  value,
  name,
  rows,
  onChange,
  InputLabelProps,
  maxLength,
  disabled,
  searchPxRef,
  onKeyPress,
  onKeyDown,
  helperText,
  multiline,
  color,
  gridXS,
  gridMD,
  grid,
  borderColor,
  onClick,
  sx,
  InputProps, // Agregamos InputProps como prop
  onBlur, //Agregamos la interacción para dispositivos móviles
  backgroundColor,
  colorTitle,
  title,
  autoComplete,
}) => {
  /**
   * Hook que obtiene el tema actual utilizando `useTheme` de Material-UI.
   * @type {Theme} El tema actual utilizado en la aplicación, que incluye configuraciones de color, tipografía, etc.
   */
  const theme = useTheme();

  return (
    <Grid size={{ xs: gridXS, md: gridMD, xl: grid, lg: grid }}>
      {title && (
        <CustomText variant="body1" color={colorTitle} sx={design.TextTitle}>
          {title}
        </CustomText>
      )}
      <CssTextField
        ref={searchPxRef}
        style={{
          width: width || "98%",
          color: color || theme.palette.mode,
          backgroundColor: backgroundColor,
          ...sx,
        }}
        label={label}
        id={name}
        type={type}
        variant="outlined"
        required={required}
        value={value}
        onChange={onChange}
        name={name}
        multiline={multiline}
        rows={rows}
        InputLabelProps={{
          shrink: InputLabelProps,
        }}
        InputProps={InputProps} // Pasamos InputProps al componente
        inputProps={{
          maxLength: maxLength, // maxLength se maneja como parte de inputProps
        }}
        disabled={disabled}
        onKeyPress={onKeyPress}
        onKeyDown={onKeyDown}
        onBlur={onBlur} // Detecta cuando se pirde el foco del input
        // onTouchStart={handleTouchStart} // Detecta cuando se empieza a tocar el campo
        onTouchEnd={onBlur} // Detecta cuando se deja de tocar el campo
        onClick={onClick}
        helper={helperText}
        autoComplete={autoComplete}
      />
      {helperText && (
        <CustomText
          variant="body1"
          text={helperText}
          sx={{ color: borderColor, ...design.Text }}
        >
          {helperText}
        </CustomText>
      )}
    </Grid>
  );
};

export default CustomTextField;

const design = {
  Text: {
    fontSize: 12,
    textAlign: "right",
    marginRight: 1,
    fontWeight: 600,
    fontFamily: "Plus Jakarta Sans",
    mt: 1,
  },
  TextTitle: {
    fontSize: 14,
    textAlign: "left",
    marginRight: 1,
    fontWeight: 400,
    fontFamily: "Plus Jakarta Sans",
    marginTop: 2,
  },
};
